import { ImagePlus, Trash2, Upload } from "lucide-react";
import { type DragEvent, useCallback, useRef, useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";

interface ScreenshotDropZoneProps {
  value: string | null;
  onChange: (value: string | null) => void;
}

const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4 MB

export function ScreenshotDropZone({ value, onChange }: ScreenshotDropZoneProps) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback(
    (file: File) => {
      setError(null);

      if (!file.type.startsWith("image/")) {
        setError("Only image files are accepted.");
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setError("Image exceeds the 4 MB size limit.");
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        if (result.startsWith("data:image/")) {
          onChange(result);
        } else {
          setError("Failed to encode image.");
        }
      };
      reader.onerror = () => setError("Failed to read file.");
      reader.readAsDataURL(file);
    },
    [onChange],
  );

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile],
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
      if (inputRef.current) inputRef.current.value = "";
    },
    [processFile],
  );

  const handleRemove = useCallback(() => {
    onChange(null);
    setError(null);
  }, [onChange]);

  if (value) {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
        <div className="mb-3 flex items-start justify-between">
          <div className="flex items-center gap-2">
            <ImagePlus className="h-4 w-4 text-primary" />
            <span className="font-medium text-sm">Screenshot attached</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleRemove}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
        <img
          src={value}
          alt="Bug screenshot preview"
          className="max-h-48 rounded-md border object-contain"
        />
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        className={cn(
          "flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors",
          dragActive
            ? "border-primary bg-primary/10 text-foreground"
            : "border-input text-muted-foreground hover:border-primary/50 hover:text-foreground",
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <Upload className="h-6 w-6" />
        <span className="text-sm">
          Drop a screenshot here, or click to browse
        </span>
        <span className="text-muted-foreground text-xs">
          PNG, JPG, or WebP — max 4 MB
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />
      {error && <p className="mt-2 text-destructive text-sm">{error}</p>}
    </div>
  );
}
